import { visit } from "@hotwired/turbo";
import { useEffect, useState } from "react";
import Breadcrumbs from "../shared/Breadcrumbs";

interface Brain {
  id: string;
  name: string;
  content: string;
  enabled: boolean;
  avatarId: string;
  createdAt: string;
  updatedAt: string;
}

interface AvatarProps {
  id: string;
  name: string;
  liveavatarAvatarId?: string;
  elevenlabsVoiceId?: string;
  createdAt: string;
  updatedAt: string;
}

interface AvatarShowProps {
  avatar: AvatarProps;
}

interface CuratedModel {
  name: string;
  modelId: string;
  provider: string;
}

const CURATED_MODELS: CuratedModel[] = [
  {
    name: "Claude Sonnet 4",
    modelId: "anthropic/claude-sonnet-4",
    provider: "Anthropic",
  },
  { name: "GPT-4.1", modelId: "openai/gpt-4.1", provider: "OpenAI" },
  { name: "GPT-4.1 Nano", modelId: "openai/gpt-4.1-nano", provider: "OpenAI" },
  {
    name: "Gemini 2.5 Flash",
    modelId: "google/gemini-2.5-flash",
    provider: "Google",
  },
  {
    name: "Gemini 2.5 Pro",
    modelId: "google/gemini-2.5-pro",
    provider: "Google",
  },
  { name: "Qwen3 Max", modelId: "qwen/qwen3-max", provider: "Qwen" },
];

export default function AvatarShow({ avatar }: AvatarShowProps) {
  const [brains, setBrains] = useState<Brain[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModelDropdownOpen, setIsModelDropdownOpen] = useState(false);

  useEffect(() => {
    fetchBrains();
  }, [avatar.id]);

  const fetchBrains = async () => {
    try {
      const response = await fetch(`/api/v1/brains?avatar_id=${avatar.id}`);
      const data = await response.json();
      // API already filters by avatar_id, no need to filter again
      setBrains(data);
    } catch (error) {
      console.error("Error fetching brains:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    visit(`/avatars/${avatar.id}/edit`);
  };

  const handleBack = () => {
    visit("/dashboard/index");
  };

  const handleStartChat = async (modelId?: string) => {
    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      const response = await fetch("/chats", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({
          chat: {
            avatar_id: avatar.id,
            prompt: " ",
            model: modelId || null,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        visit(`/chats/${data.id}`);
      }
    } catch (error) {
      console.error("Error creating chat:", error);
    }
  };

  const handleTalkToAvatar = () => {
    visit(`/avatars/${avatar.id}/stream`);
  };

  const isStreamingEnabled = avatar.liveavatarAvatarId;

  const handleDeleteBrain = async (brainId: string) => {
    if (!confirm("Are you sure you want to delete this brain?")) return;

    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      const response = await fetch(`/api/v1/brains/${brainId}`, {
        method: "DELETE",
        headers: {
          "X-CSRF-Token": csrfToken || "",
        },
      });

      if (response.ok) {
        setBrains(brains.filter((brain) => brain.id !== brainId));
      }
    } catch (error) {
      console.error("Error deleting brain:", error);
    }
  };

  const handleToggleBrain = async (brainId: string) => {
    try {
      const csrfToken = document
        .querySelector('meta[name="csrf-token"]')
        ?.getAttribute("content");

      const response = await fetch(`/api/v1/brains/${brainId}/toggle`, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": csrfToken || "",
        },
      });

      if (response.ok) {
        const updatedBrain = await response.json();
        setBrains(
          brains.map((brain) => (brain.id === brainId ? updatedBrain : brain))
        );
      }
    } catch (error) {
      console.error("Error toggling brain:", error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumbs */}
      <Breadcrumbs items={[{ label: avatar.name }]} />

      {/* Header with actions */}
      <div className="space-y-4">
        {/* Title and secondary actions */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <h1 className="text-2xl md:text-3xl font-bold">{avatar.name}</h1>
          <div className="join join-horizontal">
            <button
              onClick={handleBack}
              className="btn btn-outline btn-sm join-item"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 sm:mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              <span className="hidden sm:inline">Back</span>
            </button>
            <button
              onClick={handleEdit}
              className="btn btn-ghost btn-sm join-item"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-4 w-4 sm:mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                />
              </svg>
              <span className="hidden sm:inline">Edit</span>
            </button>
          </div>
        </div>

        {/* Primary action buttons */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
          {/* Text Chat Section */}
          <div className="dropdown flex-1 sm:flex-initial">
            <div className="join w-full sm:w-auto">
              <button
                onClick={() => handleStartChat()}
                className="btn btn-primary join-item flex-1 sm:flex-initial"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 sm:mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                  />
                </svg>
                <span className="hidden sm:inline">Start Text Chat</span>
                <span className="sm:hidden">Text Chat</span>
              </button>
              <button
                onClick={() => setIsModelDropdownOpen(!isModelDropdownOpen)}
                className="btn btn-primary join-item"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
            </div>
            {isModelDropdownOpen && (
              <ul
                tabIndex={0}
                className="dropdown-content menu p-2 shadow-lg bg-base-100 rounded-box w-72 mt-2 z-50 border border-base-300 left-1/2 -translate-x-1/2"
              >
                <li className="menu-title">
                  <span className="text-xs font-semibold">Select AI Model</span>
                </li>
                {CURATED_MODELS.map((model) => (
                  <li key={model.modelId}>
                    <button
                      onClick={() => {
                        handleStartChat(model.modelId);
                        setIsModelDropdownOpen(false);
                      }}
                      className="flex flex-col items-start py-3"
                    >
                      <span className="font-semibold">{model.name}</span>
                      <span className="text-xs text-base-content/60">
                        {model.provider}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Video Avatar Section */}
          <button
            onClick={handleTalkToAvatar}
            disabled={!isStreamingEnabled}
            className="btn btn-secondary gap-2 flex-1 sm:flex-initial"
            title={
              !isStreamingEnabled
                ? "Configure HeyGen Avatar ID and Voice ID to enable streaming"
                : "Start a live video conversation with the avatar"
            }
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z"
              />
            </svg>
            <span className="hidden sm:inline">Start Video Avatar</span>
            <span className="sm:hidden">Video Avatar</span>
          </button>

          {/* Knowledge Base Section */}
          <button
            onClick={() => visit(`/avatars/${avatar.id}/knowledge_base`)}
            className="btn btn-accent gap-2 flex-1 sm:flex-initial"
            title="Search the avatar's knowledge base"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
              />
            </svg>
            <span className="hidden sm:inline">Knowledge Base</span>
            <span className="sm:hidden">Knowledge</span>
          </button>
        </div>
      </div>

      {/* Brains Section */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <div className="flex justify-between items-center mb-4">
            <h2 className="card-title">Brains</h2>
            <a
              href={`/brains/new?avatar_id=${avatar.id}`}
              className="btn btn-sm btn-primary"
            >
              New Brain
            </a>
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <span className="loading loading-spinner loading-lg"></span>
            </div>
          ) : brains.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 mb-4">No brains yet</p>
              <a
                href={`/brains/new?avatar_id=${avatar.id}`}
                className="btn btn-sm btn-primary"
              >
                Create First Brain
              </a>
            </div>
          ) : (
            <div className="space-y-4">
              {brains.map((brain) => (
                <div key={brain.id} className="card bg-base-200">
                  <div className="card-body">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{brain.name}</h3>
                        <p className="text-sm text-gray-600 mt-2 whitespace-pre-wrap">
                          {brain.content.length > 200
                            ? `${brain.content.substring(0, 200)}...`
                            : brain.content}
                        </p>
                      </div>
                      <div className="flex gap-4 items-center">
                        <div className="form-control">
                          <label className="label cursor-pointer gap-2">
                            <span className="label-text">
                              {brain.enabled ? "Enabled" : "Disabled"}
                            </span>
                            <input
                              type="checkbox"
                              className="toggle toggle-success"
                              checked={brain.enabled}
                              onChange={() => handleToggleBrain(brain.id)}
                            />
                          </label>
                        </div>
                        <button
                          onClick={() => visit(`/brains/${brain.id}/edit`)}
                          className="btn btn-sm btn-ghost"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteBrain(brain.id)}
                          className="btn btn-sm btn-error"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Avatar Details */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title">Avatar Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="label">
                <span className="label-text font-semibold">ID</span>
              </label>
              <p className="text-base-content">{avatar.id}</p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Name</span>
              </label>
              <p className="text-base-content">{avatar.name || "Not set"}</p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Created At</span>
              </label>
              <p className="text-base-content">
                {new Date(avatar.createdAt).toLocaleString()}
              </p>
            </div>

            <div>
              <label className="label">
                <span className="label-text font-semibold">Updated At</span>
              </label>
              <p className="text-base-content">
                {new Date(avatar.updatedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
